import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { getAdminInfo, getUserInfo } from "../utils/user.util";
import { getAdminToken, getToken } from "../utils/auth.util";
import { Permission } from "../types/permission.types";

interface User {
  _id?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: string;
  permissions?: Permission[];
  adminName?: string;
  emailId?: string;
  status?: {
    current: 'ACTIVE' | 'BLOCKED' | 'DELETED';
    [key: string]: any;
  };
  [key: string]: any;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
  isAdmin: boolean;
  permissions: Permission[];
  isLoading: boolean;
  logout: () => void;
  refreshUser: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function resolveAuthState(): Omit<AuthContextType, 'logout' | 'refreshUser'> {
  // Check for admin authentication first
  const adminInfo = getAdminInfo();
  const adminToken = getAdminToken();

  if (adminInfo && adminToken) {
    const isAdminSuper = adminInfo.role === 'superadmin' ||
                         adminInfo.role === 'SuperAdmin' ||
                         adminInfo.isSuperAdmin === true ||
                         adminInfo.adminType === 'super';
    return {
      user: adminInfo,
      isAuthenticated: true,
      isAdmin: true,
      isSuperAdmin: isAdminSuper,
      permissions: adminInfo.permissions || [],
      isLoading: false,
    };
  }

  // Check for regular user authentication
  const userInfo = getUserInfo();
  const token = getToken();

  if (userInfo && token) {
    const isUserSuperAdmin = userInfo.role === 'superadmin' ||
                             userInfo.role === 'SuperAdmin' ||
                             userInfo.isSuperAdmin === true;
    return {
      user: userInfo,
      isAuthenticated: true,
      isSuperAdmin: isUserSuperAdmin,
      isAdmin: isUserSuperAdmin || userInfo.role === 'admin',
      permissions: userInfo.permissions || [],
      isLoading: false,
    };
  }

  // No authentication found
  return {
    user: null,
    isAuthenticated: false,
    isAdmin: false,
    isSuperAdmin: false,
    permissions: [],
    isLoading: false,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<Omit<AuthContextType, 'logout' | 'refreshUser'>>({
    user: null,
    isAuthenticated: false,
    isSuperAdmin: false,
    isAdmin: false,
    permissions: [],
    isLoading: true,
  });

  const refreshUser = useCallback(() => {
    setState(resolveAuthState());
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const logout = useCallback(() => {
    localStorage.clear();
    setState({
      user: null,
      isAuthenticated: false,
      isAdmin: false,
      isSuperAdmin: false,
      permissions: [],
      isLoading: false,
    });
  }, []);

  const value: AuthContextType = {
    ...state,
    logout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
